/**
 * Edit mode's model (DOP-622): a memo section as an editor document and
 * back, and what changed while editing.
 *
 * The document is built from the same `EditItem`s the one-section editor
 * uses (`sectionEditModel`), and read back into them, so saving goes
 * through the same `opsOf`. An edit is always a whole script against the
 * GENERATED text, which is what the server stores and re-applies on the
 * next run.
 *
 * Shape of a section's document:
 *  - a prose paragraph is a `paragraph` node carrying its generated anchor.
 *    A bold run at its start is the lead-in, and source chips follow the
 *    sentences they cite (read-only, dropped when read back);
 *  - a bullets paragraph is a `bulletList` carrying the anchor, one list
 *    item per sentence, and a first item wholly in bold is the lead-in;
 *  - a table is a `memoTable` block carrying the table itself (`table`) and
 *    how it read when editing started (`startTable`). Its cells are typed in
 *    place, rows and columns added or taken out (DOP-637).
 *
 * A paragraph split with Enter keeps no anchor (`keepOnSplit: false` in the
 * editor), so its second half reads as the person's addition.
 */

import { stripMarks } from "./inlineMarks";
import type { JSONContent } from "@tiptap/react";

import type {
	DealScreeningChart,
	DealScreeningParagraph,
	DealScreeningSection,
	DealScreeningSourceNote,
} from "../../types/dealScreening";
import { type DocumentSource, fromSavedSource, sourceKey, sourceText } from "./documentSource";
import { type EditItem, currentTable, itemsOf, paragraphText } from "./sectionEditModel";
import { chartText } from "./chartModel";
import { diffText } from "./memoDiff";
import {
	type FormatSpan,
	formattedNodes,
	fromStoredOffsets,
	runsOf,
	shifted,
	spansKey,
	trimmed,
} from "./memoFormatModel";
import { type EditTable, editTableOf, setCellNote, tableKey, tableText } from "./tableEditModel";

/**
 * Sections a person types in: every one but the financial summary, which is
 * built from the figures and changed through them (DOP-637). The questions
 * and a section the run left out take a person's text like any other.
 */
export const isEditableSection = (section: DealScreeningSection): boolean =>
	section.key === "diligence_questions" ||
	(section.written_by !== "code" && section.key !== "financial_summary");

/** Text, or a paragraph's lead-in in its own mark (codex r3): bold alone is formatting. */
const textNode = (text: string, lead = false): JSONContent =>
	lead ? { type: "text", text, marks: [{ type: "leadIn" }] } : { type: "text", text };

type Sentence = DealScreeningParagraph["sentences"][number];

/**
 * The document a person's own sentence names (DOP-652), or null. A sentence
 * the run wrote keeps its source through the save by its wording and carries
 * a chip; a person's has to say its source again with every save, so it
 * carries it on its words.
 */
const ownDocument = (sentence: Sentence): DocumentSource | null =>
	sentence.edited ? fromSavedSource(sentence.source) : null;

/** The mark on a person's words that says where they came from. */
export const sourceMark = (note: string, source: DocumentSource | null = null) => ({
	type: "sourceNote",
	// A note alone is stored as it always was.
	attrs: source ? { note, source } : { note },
});

/**
 * A sentence's text as editor nodes: its formatting as marks (DOP-641), and
 * where a person said it came from on every piece.
 */
const sentenceNodes = (sentence: Sentence): JSONContent[] => {
	const document = ownDocument(sentence);
	const note = sentence.source?.kind === "user" || document ? (sentence.source?.note ?? "") : "";
	return formattedNodes(
		sentence.text,
		fromStoredOffsets(sentence.text, sentence.format),
		note || document ? [sourceMark(note, document)] : [],
	);
};

/** A sentence's chip: a file's number; a person's source is on their words instead. */
const sentenceChip = (
	sentence: Sentence,
	numberOf: (anchor: string | null | undefined) => number | null,
): JSONContent[] =>
	sentence.source?.kind === "user" || ownDocument(sentence) ? [] : chip(numberOf(sentence.source?.anchor));

const chip = (number: number | null | undefined): JSONContent[] =>
	number ? [{ type: "sourceChip", attrs: { number } }] : [];

/**
 * The editor document for a section, from what the reader sees. `numberOf`
 * gives a sentence's source number, so the chips a reader knows stay in
 * place while editing.
 */
export function sectionDoc(
	section: DealScreeningSection,
	numberOf: (anchor: string | null | undefined) => number | null,
	/** Makes `baseId`s unique when sections share one document. */
	prefix = "",
): JSONContent {
	const items = itemsOf(section);
	const content: JSONContent[] = items.map((item, index) => {
		const paragraph: DealScreeningParagraph | undefined = section.paragraphs[index];
		const sentences = paragraph?.sentences ?? [];
		if (item.table) {
			const table = editTableOf(item.table);
			return {
				type: "memoTable",
				attrs: { anchor: item.anchor, caption: item.table.caption ?? "Table", table, startTable: table },
			};
		}
		if (item.chart) {
			return { type: "memoChart", attrs: { anchor: item.anchor, chart: item.chart } };
		}
		if (item.kind === "bullets") {
			const bullets: JSONContent[] = sentences.map((sentence) => ({
				type: "listItem",
				content: [
					{
						type: "paragraph",
						content: [...sentenceNodes(sentence), ...sentenceChip(sentence, numberOf)],
					},
				],
			}));
			if (item.lead) {
				bullets.unshift({
					type: "listItem",
					content: [{ type: "paragraph", content: [textNode(item.lead, true)] }],
				});
			}
			return {
				// A list a person numbered (DOP-641).
				type: paragraph?.ordered ? "orderedList" : "bulletList",
				attrs: { anchor: item.anchor, baseId: `${prefix}b${index}` },
				content: bullets,
			};
		}
		const body: JSONContent[] = [];
		if (item.lead) body.push(textNode(item.lead, true), textNode(" "));
		sentences.forEach((sentence, position) => {
			if (position > 0) body.push(textNode(" "));
			body.push(...sentenceNodes(sentence));
			body.push(...sentenceChip(sentence, numberOf));
		});
		if (sentences.length === 0 && item.text) body.push(textNode(item.text));
		return { type: "paragraph", attrs: { anchor: item.anchor, baseId: `${prefix}b${index}` }, content: body };
	});
	return { type: "doc", content: content.length > 0 ? content : [{ type: "paragraph" }] };
}

/** A node's plain text: chips carry none. */
export const plain = (node: JSONContent): string =>
	node.type === "text"
		? (node.text ?? "")
		: node.type === "hardBreak"
			? "\n"
			: (node.content ?? []).map(plain).join("");

/**
 * The sources a block carries: each run of text under one note (DOP-637) or
 * one document (DOP-652), as the words and where they came from. Runs next
 * to each other under the same source are one quote.
 */
export function notesOf(node: JSONContent): DealScreeningSourceNote[] {
	const notes: DealScreeningSourceNote[] = [];
	let open: DealScreeningSourceNote | null = null;
	const same = (a: DealScreeningSourceNote, note: string, source: DocumentSource | null) =>
		a.note === note && sourceKey(a.source) === sourceKey(source);
	const walk = (child: JSONContent) => {
		if (child.type === "text") {
			const attrs = (child.marks ?? []).find((mark) => mark.type === "sourceNote")?.attrs;
			const note = ((attrs?.note as string | undefined) ?? "").trim();
			const source = (attrs?.source as DocumentSource | null | undefined) ?? null;
			if ((note || source) && open && same(open, note, source)) open.quote += child.text ?? "";
			else if (note || source) {
				open = { quote: child.text ?? "", note, ...(source ? { source } : {}) };
				notes.push(open);
			} else open = null;
			return;
		}
		if (child.type !== "sourceChip") open = null;
		(child.content ?? []).forEach(walk);
	};
	(node.content ?? []).forEach(walk);
	return notes
		.map((n) => ({ ...n, quote: n.quote.replace(/\s+/g, " ").trim(), note: n.note.trim() }))
		.filter((n) => n.quote && (n.note || n.source));
}

/**
 * A paragraph node's lead-in (its leadIn run), and the text after it with the
 * formatting in it (DOP-641). Bold a person gave the first words is not a
 * lead-in (codex r3): those words stay in the sentence, bold.
 */
function splitLead(node: JSONContent): { lead: string; text: string; spans: FormatSpan[] } {
	const parts = node.content ?? [];
	let lead = "";
	let index = 0;
	while (
		index < parts.length &&
		parts[index].type === "text" &&
		(parts[index].marks ?? []).some((mark) => mark.type === "leadIn")
	) {
		lead += parts[index].text ?? "";
		index += 1;
	}
	const rest = runsOf(parts.slice(index));
	return { lead: lead.trim(), ...trimmed(rest.text, rest.spans) };
}

const isList = (node: JSONContent): boolean => node.type === "bulletList" || node.type === "orderedList";

/** The editor document read back into items `opsOf` turns into a script. */
export function itemsFromDoc(section: DealScreeningSection, doc: JSONContent): EditItem[] {
	const generated = new Map(
		(section.generated_paragraphs ?? section.paragraphs).map((p) => [p.anchor ?? "", p]),
	);
	const tables = new Map(
		section.paragraphs.filter((p) => p.kind === "table").map((p) => [p.anchor ?? "", p]),
	);
	const generatedTables = new Map(
		(section.generated_paragraphs ?? section.paragraphs)
			.filter((p) => p.kind === "table")
			.map((p) => [p.anchor ?? "", editTableOf(p.table)]),
	);
	const origin = (anchor: string | null) => {
		const paragraph = anchor ? generated.get(anchor) : undefined;
		return {
			generatedLead: paragraph?.lead ?? "",
			generatedText: paragraph ? paragraphText(paragraph) : "",
			generatedKind: (paragraph?.kind === "bullets" ? "bullets" : "prose") as "prose" | "bullets",
			generatedOrdered: Boolean(paragraph?.ordered),
		};
	};
	const items: EditItem[] = [];
	for (const node of doc.content ?? []) {
		const anchor = (node.attrs?.anchor as string | null | undefined) ?? null;
		if (node.type === "memoChart") {
			const chart = node.attrs?.chart as DealScreeningChart | null | undefined;
			if (!chart) continue;
			items.push({ anchor, lead: "", text: "", kind: "prose", table: null, chart, ...origin(anchor) });
			continue;
		}
		if (node.type === "memoTable") {
			const table = tables.get(anchor ?? "");
			const now = (node.attrs?.table as EditTable | null | undefined) ?? editTableOf(table?.table);
			if (!table?.table && !now) continue;
			items.push({
				anchor,
				lead: "",
				text: "",
				kind: "prose",
				table: table?.table ?? { caption: now?.caption ?? null, columns: [], rows: [] },
				...origin(anchor),
				tableNow: now,
				generatedTable: generatedTables.get(anchor ?? "") ?? null,
			});
			continue;
		}
		if (isList(node)) {
			const rows = (node.content ?? []).map((item) => {
				const inner = item.content?.[0] ?? {};
				const whole = runsOf(item.content ?? []);
				return { ...splitLead(inner), whole: trimmed(whole.text, whole.spans) };
			});
			const first = rows[0];
			const leadRow = first && first.lead !== "" && first.text === "";
			// One line per bullet, each line's formatting moved to where the
			// line sits in the list's text.
			let text = "";
			const formats: FormatSpan[] = [];
			(leadRow ? rows.slice(1) : rows).forEach((row) => {
				if (!row.whole.text) return;
				if (text) text += "\n";
				formats.push(...shifted(row.whole.spans, text.length, text.length + row.whole.text.length));
				text += row.whole.text;
			});
			if (!text && !leadRow) continue;
			items.push({
				anchor,
				lead: leadRow ? first.lead : "",
				text,
				kind: "bullets",
				ordered: node.type === "orderedList",
				formats,
				table: null,
				...origin(anchor),
				notes: notesOf(node),
				baseId: (node.attrs?.baseId as string | null | undefined) ?? null,
			});
			continue;
		}
		const { lead, text, spans } = splitLead(node);
		if (lead === "" && text === "") continue; // an empty line is not a paragraph
		items.push({
			anchor,
			lead,
			text,
			kind: "prose",
			formats: spans,
			table: null,
			...origin(anchor),
			notes: notesOf(node),
			baseId: (node.attrs?.baseId as string | null | undefined) ?? null,
		});
	}
	return items;
}

/** Items as a comparable string: whether a section changed at all. */
export const itemsKey = (items: EditItem[]): string =>
	JSON.stringify(
		items.map((item) => [
			item.anchor,
			item.lead,
			item.kind,
			item.text.replace(/\s+/g, " ").trim(),
			item.table ? tableKey(currentTable(item)) : "",
			item.chart ? JSON.stringify([item.chart.chart_type, item.chart.title ?? "", item.chart.data]) : "",
			notesKey(item),
			spansKey(item.formats),
			Boolean(item.ordered),
		]),
	);

/** The layout and formatting of an item, apart from its words (DOP-641). */
const looksKey = (item: EditItem): string => `${item.kind}|${Boolean(item.ordered)}|${spansKey(item.formats)}`;

const notesKey = (item: EditItem): string =>
	(item.notes ?? []).map((n) => `${n.quote}\u0000${n.note}\u0000${sourceKey(n.source)}`).join("\u0001");

/**
 * The item's words a note does not source: where a typed figure still wants
 * a note. A table leaves out the cells a person gave a note.
 */
const unnotedText = (item: EditItem): string => {
	if (item.table) {
		const table = currentTable(item);
		if (!table) return "";
		return [
			table.caption ?? "",
			...table.columns,
			...table.rows.flatMap((row, r) =>
				row.filter((_, c) => !table.notes?.[`${r}:${c}`] && !table.sources?.[`${r}:${c}`]),
			),
		].join(" ");
	}
	return (item.notes ?? []).reduce((text, n) => text.split(n.quote).join(" "), itemText(item));
};

export type ChangeKind = "added" | "edited" | "removed";

export interface MemoChange {
	sectionKey: string;
	sectionTitle: string;
	kind: ChangeKind;
	/** The paragraph's anchor at the start, or null for a person's addition. */
	anchor: string | null;
	/** A person's own paragraph edited in place: its block at the start. */
	baseId?: string | null;
	/** A short quote of what changed. */
	summary: string;
	/** The whole paragraph's text: now for added and edited, before for removed. */
	text: string;
	/** Numbers typed in this change, which the memo cannot source. */
	newNumbers: string[];
	/** A section renamed, rather than its text changed (DOP-641). */
	rename?: boolean;
	/** A whole section added or taken out (DOP-656), rather than a paragraph. */
	section?: boolean;
}

const quote = (text: string): string => {
	const clean = text.replace(/\s+/g, " ").trim();
	return clean.length > 90 ? `${clean.slice(0, 87)}…` : clean;
};

/** "$8.0M", "44%", "2.1x", "1,200": a figure a reader would want sourced. */
const NUMBER = /(?:[$€£]\s?)?\d[\d,]*(?:\.\d+)?\s?(?:%|x|bn|b|mm|m|k|million|billion)?/gi;

/** A figure's digits: "$1.34M" and "1.34mm" are the same number written two ways. */
const figureKey = (figure: string): string => figure.replace(/[^\d.]/g, "").replace(/\.$/, "");

/** Figures in `after` that were not in `before`. */
export function newNumbers(before: string, after: string): string[] {
	const had = new Set((before.match(NUMBER) ?? []).map(figureKey));
	const found = (after.match(NUMBER) ?? [])
		.map((n) => n.trim())
		.filter((n) => /\d/.test(n) && !/^\d{4}$/.test(n)); // a bare year is not a figure
	return Array.from(new Set(found.filter((n) => !had.has(figureKey(n)))));
}

function itemText(item: EditItem): string {
	if (item.chart) return chartText(item.chart);
	return item.table ? tableText(currentTable(item)) : [item.lead, item.text].filter(Boolean).join(" ");
}

/** How the changes list names a chart: "the Revenue by year chart", or "a chart". */
const chartName = (item: EditItem): string => {
	const title = (item.chart?.title ?? "").trim();
	return title ? `the ${title} chart` : "a chart";
};

/** How the changes list names a table: "the Market size table", or "a table". */
const tableName = (item: EditItem): string => {
	const caption = (currentTable(item)?.caption ?? item.table?.caption ?? "").trim();
	return caption ? `the ${caption} table` : "a table";
};

/**
 * What changed in one section, paragraph by paragraph, against how it read
 * when editing started.
 */
export function sectionChanges(
	section: DealScreeningSection,
	start: EditItem[],
	now: EditItem[],
): MemoChange[] {
	const changes: MemoChange[] = [];
	const base = { sectionKey: section.key, sectionTitle: stripMarks(section.title) };
	const startByAnchor = new Map(start.filter((i) => i.anchor).map((i) => [i.anchor as string, i]));
	const nowAnchors = new Set(now.filter((i) => i.anchor).map((i) => i.anchor as string));
	for (const item of now) {
		if (item.chart) {
			// A chart is an addition (DOP-655): new now, unless the section
			// already had it as it reads.
			const had = start.some((s) => s.chart && itemText(s) === itemText(item));
			if (!had) {
				changes.push({
					...base,
					kind: "added",
					anchor: null,
					summary: chartName(item),
					text: itemText(item),
					newNumbers: [],
				});
			}
			continue;
		}
		if (item.table) {
			const was = item.anchor ? startByAnchor.get(item.anchor) : undefined;
			if (!was) {
				// A table with no generated one behind it (DOP-653): added now,
				// unless the section already had it as it reads.
				const had = start.some(
					(s) => !s.anchor && s.table && tableKey(currentTable(s)) === tableKey(currentTable(item)),
				);
				if (!had && !item.anchor) {
					changes.push({
						...base,
						kind: "added",
						anchor: null,
						summary: tableName(item),
						text: itemText(item),
						newNumbers: newNumbers("", unnotedText(item)),
					});
				}
				continue;
			}
			if (tableKey(currentTable(was)) === tableKey(currentTable(item))) continue;
			changes.push({
				...base,
				kind: "edited",
				anchor: item.anchor,
				summary: tableName(item),
				text: itemText(item),
				newNumbers: newNumbers(itemText(was), unnotedText(item)),
			});
			continue;
		}
		// A paragraph with no anchor (a person's, or the questions' list) is
		// the same one when its block is the same one.
		const was = item.anchor
			? startByAnchor.get(item.anchor)
			: item.baseId
				? start.find((s) => !s.anchor && s.baseId === item.baseId)
				: undefined;
		const own = !item.anchor && was !== undefined;
		if (!was) {
			// A split paragraph or a new one: the person's text. A paragraph the
			// section already had without an anchor (an earlier addition) is
			// matched by its wording instead.
			const same = start.find((s) => !s.anchor && !s.table && !s.chart && itemText(s) === itemText(item));
			if (same) continue;
			changes.push({
				...base,
				kind: "added",
				anchor: null,
				summary: quote(itemText(item)),
				text: itemText(item),
				newNumbers: newNumbers("", unnotedText(item)),
			});
			continue;
		}
		if (itemText(was).replace(/\s+/g, " ").trim() !== itemText(item).replace(/\s+/g, " ").trim()) {
			const added = diffText(itemText(was), itemText(item))
				.filter((part) => part.kind === "added")
				.map((part) => part.text)
				.join(" ");
			changes.push({
				...base,
				kind: "edited",
				anchor: item.anchor,
				...(own ? { baseId: item.baseId } : {}),
				summary: quote(added || itemText(item)),
				text: itemText(item),
				newNumbers: newNumbers(itemText(was), unnotedText(item)),
			});
		} else if (looksKey(was) !== looksKey(item)) {
			// Only the formatting or the layout changed (DOP-641).
			changes.push({
				...base,
				kind: "edited",
				anchor: item.anchor,
				...(own ? { baseId: item.baseId } : {}),
				summary:
					was.kind !== item.kind || Boolean(was.ordered) !== Boolean(item.ordered)
						? `Laid out as ${item.kind === "bullets" ? (item.ordered ? "a numbered list" : "bullets") : "a paragraph"}: "${quote(itemText(item))}"`
						: `Formatting: "${quote(itemText(item))}"`,
				text: itemText(item),
				newNumbers: [],
			});
		} else if (notesKey(was) !== notesKey(item)) {
			// Only a note on where the words came from changed.
			const note = (item.notes ?? []).find(
				(n) => !(was.notes ?? []).some((w) => w.note === n.note && sourceKey(w.source) === sourceKey(n.source)),
			);
			changes.push({
				...base,
				kind: "edited",
				anchor: item.anchor,
				...(own ? { baseId: item.baseId } : {}),
				summary: note
					? `Source for "${quote(note.quote)}": ${sourceText(note.note, note.source)}`
					: "Source taken off",
				text: itemText(item),
				newNumbers: [],
			});
		}
	}
	for (const item of start) {
		if (!item.anchor || nowAnchors.has(item.anchor)) continue;
		changes.push({
			...base,
			kind: "removed",
			anchor: item.anchor,
			summary: item.table ? tableName(item) : quote(itemText(item)),
			text: itemText(item),
			newNumbers: [],
		});
	}
	for (const item of start) {
		if (!item.chart || now.some((n) => n.chart && itemText(n) === itemText(item))) continue;
		changes.push({
			...base,
			kind: "removed",
			anchor: null,
			summary: chartName(item),
			text: itemText(item),
			newNumbers: [],
		});
	}
	for (const item of start) {
		if (item.anchor || item.table || item.chart) continue;
		if (item.baseId && now.some((n) => !n.anchor && n.baseId === item.baseId)) continue;
		if (!now.some((n) => !n.anchor && itemText(n) === itemText(item))) {
			changes.push({
				...base,
				kind: "removed",
				anchor: null,
				summary: quote(itemText(item)),
				text: itemText(item),
				newNumbers: [],
			});
		}
	}
	return changes;
}

/** A list's text with its items apart: "Lead. First. Second." */
export const listText = (node: JSONContent): string =>
	(node.content ?? []).map((item) => plain(item).trim()).filter(Boolean).join(" ");

/** A list's items at the start, apart, for tracked changes to compare item by item (DOP-641). */
export const LIST_ITEM_SEPARATOR = "\u0000";

/** Each block's text at the start, by `baseId`: what tracked changes compare to. */
export function baselineOf(doc: JSONContent): Map<string, string> {
	const baseline = new Map<string, string>();
	for (const node of doc.content ?? []) {
		const baseId = node.attrs?.baseId as string | undefined;
		if (baseId && node.type === "paragraph") baseline.set(baseId, plain(node));
		if (baseId && isList(node)) {
			baseline.set(baseId, (node.content ?? []).map((item) => plain(item)).join(LIST_ITEM_SEPARATOR));
		}
	}
	return baseline;
}

/** One block's text as the changes list quotes it. */
const blockText = (section: DealScreeningSection, block: JSONContent): string => {
	const [item] = itemsFromDoc(section, { type: "doc", content: [block] });
	return item ? itemText(item) : "";
};

/** Whether a block is the one a change is about, in the current document. */
const isChangedBlock = (section: DealScreeningSection, block: JSONContent, change: MemoChange): boolean =>
	change.anchor
		? block.attrs?.anchor === change.anchor
		: change.baseId
			? block.attrs?.baseId === change.baseId
			: !block.attrs?.baseId && blockText(section, block) === change.text;

/**
 * The document with one change undone: an edit back to how it read, an
 * addition taken out, a removed paragraph put back where it was.
 */
export function undoChange(
	section: DealScreeningSection,
	doc: JSONContent,
	startDoc: JSONContent,
	change: MemoChange,
): JSONContent {
	const blocks = [...(doc.content ?? [])];
	const start = startDoc.content ?? [];
	if (change.kind === "added") {
		const index = blocks.findIndex((block) => isChangedBlock(section, block, change));
		if (index >= 0) blocks.splice(index, 1);
	} else if (change.kind === "edited") {
		const index = blocks.findIndex((block) => isChangedBlock(section, block, change));
		const original = start.find((block) =>
			change.anchor ? block.attrs?.anchor === change.anchor : block.attrs?.baseId === change.baseId,
		);
		if (index >= 0 && original) blocks[index] = original;
	} else {
		const at = start.findIndex((block) =>
			change.anchor
				? block.attrs?.anchor === change.anchor
				: !block.attrs?.anchor && blockText(section, block) === change.text,
		);
		if (at >= 0) {
			// After the nearest block before it that is still there.
			const before = start
				.slice(0, at)
				.reverse()
				.map((block) => blocks.findIndex((b) => b.attrs?.baseId && b.attrs.baseId === block.attrs?.baseId))
				.find((index) => index >= 0);
			blocks.splice(before === undefined ? 0 : before + 1, 0, start[at]);
		}
	}
	return { ...doc, content: blocks.length > 0 ? blocks : [{ type: "paragraph" }] };
}

/** Text runs with the note marked on every place one of `needles` occurs. */
function markRuns(content: JSONContent[], needles: string[], note: string): JSONContent[] {
	const mark = sourceMark(note);
	return content.flatMap((node) => {
		if (node.type !== "text") {
			return node.content ? [{ ...node, content: markRuns(node.content, needles, note) }] : [node];
		}
		const text = node.text ?? "";
		const found = needles
			.flatMap((needle) => {
				const at: [number, number][] = [];
				for (let i = text.indexOf(needle); needle && i >= 0; i = text.indexOf(needle, i + needle.length)) {
					at.push([i, i + needle.length]);
				}
				return at;
			})
			.sort((a, b) => a[0] - b[0]);
		if (found.length === 0) return [node];
		const parts: JSONContent[] = [];
		let cursor = 0;
		for (const [from, to] of found) {
			if (from < cursor) continue;
			if (from > cursor) parts.push({ ...node, text: text.slice(cursor, from) });
			const marks = (node.marks ?? []).filter((m) => m.type !== "sourceNote");
			parts.push({ ...node, text: text.slice(from, to), marks: [...marks, mark] });
			cursor = to;
		}
		if (cursor < text.length) parts.push({ ...node, text: text.slice(cursor) });
		return parts;
	});
}

/**
 * The document with a note on where a change's typed figures came from
 * (DOP-637): each figure in the paragraph carries it as its source, as a
 * note set from the selection does. In a table, every cell holding one
 * of them does.
 */
export function addSourceNote(
	section: DealScreeningSection,
	doc: JSONContent,
	change: MemoChange,
	note: string,
): JSONContent {
	const clean = note.replace(/\s+/g, " ").trim();
	const needles = change.newNumbers;
	const content = (doc.content ?? []).map((block) => {
		if (!isChangedBlock(section, block, change) || !clean) return block;
		if (block.type === "memoChart") return block;
		if (block.type === "memoTable") {
			const table = block.attrs?.table as EditTable | null | undefined;
			if (!table) return block;
			let next = table;
			table.rows.forEach((row, r) =>
				row.forEach((cell, c) => {
					if (needles.some((n) => cell.includes(n))) next = setCellNote(next, r, c, clean);
				}),
			);
			return { ...block, attrs: { ...block.attrs, table: next } };
		}
		return { ...block, content: markRuns(block.content ?? [], needles, clean) };
	});
	return { ...doc, content };
}

/** Whether a change's typed figures already say where they came from. */
export const hasSourceNote = (change: MemoChange): boolean =>
	change.newNumbers.length === 0 || /\(Source: [^)]+\)/.test(change.text);

// ------------------------------------------------------------ the whole memo

/**
 * The whole memo as ONE document (DOP-622): every section's heading as a
 * fixed line, the prose a person can type in, and the sections built from
 * the figures (the financial summary, the questions) as read-only blocks in
 * their place. A person edits it top to bottom like any document. The
 * headings stay fixed, so each paragraph still belongs to its section when
 * the edits are saved.
 */
export function memoDoc(
	sections: DealScreeningSection[],
	numberOf: (anchor: string | null | undefined) => number | null,
	readOnlyNote: (section: DealScreeningSection) => string,
): JSONContent {
	const content: JSONContent[] = [];
	for (const section of sections) {
		const editable = isEditableSection(section);
		content.push({
			type: "memoSectionHeading",
			// The title is typed in place (DOP-641); `startTitle` is how it read.
			// A person's own section (DOP-656) can be taken out again.
			attrs: {
				key: section.key,
				title: section.title,
				startTitle: section.title,
				editable,
				user: section.written_by === "user",
			},
		});
		if (editable) {
			const blocks = sectionDoc(section, numberOf, `${section.key}:`).content ?? [];
			content.push(...(blocks.length > 0 ? blocks : [{ type: "paragraph" }]));
		} else {
			content.push({ type: "memoReadonly", attrs: { key: section.key, note: readOnlyNote(section) } });
		}
	}
	return { type: "doc", content };
}

/** The document's section headings, in order (DOP-656). */
export interface DocHeading {
	key: string;
	title: string;
	/** A person's own section: added in this draft, or saved on an earlier version. */
	user: boolean;
}

export function headingsOf(doc: JSONContent): DocHeading[] {
	return (doc.content ?? [])
		.filter((node) => node.type === "memoSectionHeading" && node.attrs?.key)
		.map((node) => ({
			key: String(node.attrs?.key),
			title: String(node.attrs?.title ?? "").replace(/\s+/g, " ").trim(),
			user: Boolean(node.attrs?.user),
		}));
}

/** The key a section added in this draft gets until it is saved (the server names it then). */
export const NEW_SECTION_PREFIX = "new-";

/** A section added in this draft, as the editor holds it. */
export interface AddedSection {
	key: string;
	title: string;
	/** The saved section it follows, or null for the end of the memo. */
	after: string | null;
	items: EditItem[];
	/** The section as the editor's model needs one: a title, no generated body. */
	section: DealScreeningSection;
}

/** A section with no generated body, for the editor to read a draft's blocks against. */
export const emptySection = (key: string, title: string): DealScreeningSection => ({
	key,
	title,
	status: "written",
	order: 0,
	omitted_reason: null,
	deal_specific: false,
	written_by: "user",
	base_body_sha: "",
	paragraphs: [],
});

/**
 * What the draft does to the memo's sections (DOP-656): the sections a
 * person added (headings the saved memo does not have), each with its
 * blocks read as items, and the keys of their own sections they took out.
 */
export function sectionChangesOf(
	run: { sections: DealScreeningSection[] },
	doc: JSONContent,
): { added: AddedSection[]; removed: string[] } {
	const saved = new Map(run.sections.map((section) => [section.key, section]));
	const headings = headingsOf(doc);
	const parts = splitMemoDoc(doc);
	const added: AddedSection[] = [];
	let lastSaved: string | null = null;
	headings.forEach((heading) => {
		if (saved.has(heading.key)) {
			lastSaved = heading.key;
			return;
		}
		if (!heading.key.startsWith(NEW_SECTION_PREFIX)) return;
		const section = emptySection(heading.key, heading.title);
		added.push({
			key: heading.key,
			title: heading.title || "Untitled section",
			after: lastSaved,
			items: itemsFromDoc(section, parts[heading.key] ?? { type: "doc" }),
			section,
		});
	});
	const present = new Set(headings.map((heading) => heading.key));
	const removed = run.sections
		.filter((section) => section.written_by === "user" && !present.has(section.key))
		.map((section) => section.key);
	return { added, removed };
}

/** Each section's title as the document has it now (DOP-641). */
export function titlesOf(doc: JSONContent): Record<string, string> {
	const titles: Record<string, string> = {};
	for (const node of doc.content ?? []) {
		if (node.type === "memoSectionHeading" && node.attrs?.key) {
			titles[String(node.attrs.key)] = String(node.attrs.title ?? "");
		}
	}
	return titles;
}

/** The whole document cut back into each editable section's own blocks. */
export function splitMemoDoc(doc: JSONContent): Record<string, JSONContent> {
	const parts: Record<string, JSONContent> = {};
	let current: string | null = null;
	for (const node of doc.content ?? []) {
		if (node.type === "memoSectionHeading") {
			current = node.attrs?.editable ? String(node.attrs.key) : null;
			if (current) parts[current] = { type: "doc", content: [] };
			continue;
		}
		if (node.type === "memoReadonly" || !current) continue;
		parts[current].content?.push(node);
	}
	return parts;
}

/** The whole document with one section's blocks replaced. */
export function withSectionDoc(doc: JSONContent, sectionKey: string, part: JSONContent): JSONContent {
	const out: JSONContent[] = [];
	let inside = false;
	for (const node of doc.content ?? []) {
		if (node.type === "memoSectionHeading") {
			if (inside) inside = false;
			out.push(node);
			if (node.attrs?.key === sectionKey) {
				inside = true;
				out.push(...(part.content ?? []));
			}
			continue;
		}
		if (!inside) out.push(node);
	}
	return { ...doc, content: out };
}
